import { Alert, Box, Grid, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GoogleMap, Marker, OverlayView } from '@react-google-maps/api';

import GoogleMapsReady from '../components/GoogleMapsReady';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { DataAPIContext } from '../api';
import { GET_ONE } from '../api/dataProvider';
import FixedLinearProgress from '../components/FixedLinearProgress';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { formatTourOptionLabel } from '../components/tourOptionLabel';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';

const POLL_INTERVAL = 15; // seconds

// CP-8024 color spec: Guests are blue; all other users (guides) are red.
const GUEST_COLOR = '#007AFF';
const GUIDE_COLOR = '#EB5757';
const DARK_GRAY = '#616161';
const LIGHT_GRAY = '#BDBDBD';

// CP-8024 staleness tiers (minutes since last location ping).
const FRESH_MINUTES = 5;
const RECENT_MINUTES = 30;
const MAX_AGE_MINUTES = 180;

// Don't zoom in past city level when fitting the map to the pins.
const MAX_FIT_ZOOM = 12;

// Stable references: @react-google-maps/api shallow-compares props on every
// render and re-applies them to the map instance (e.g. map.setCenter), so
// inline object literals here would snap the map back to the default view on
// each poll tick or marker click.
const DEFAULT_CENTER = { lat: 20, lng: 0 };
const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };

const mapStyles = [
    { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
    { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
    { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
    { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'transit.station', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
];

const MAP_OPTIONS = {
    fullscreenControl: true,
    styles: mapStyles,
    clickableIcons: false,
};

const svgDataUri = (svg) => `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

// Fresh (< 5 min): solid colored dot with a soft halo.
const createSolidDotIcon = (color) => ({
    url: svgDataUri(`
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="8" fill="${color}" />
        <circle cx="12" cy="12" r="11" stroke="${color}" stroke-width="2" fill="none" opacity="0.5" />
    </svg>`),
});

// Stale: gray dot with a blue/red outline.
const createOutlinedDotIcon = (fill, outline) => ({
    url: svgDataUri(`
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="8" fill="${fill}" stroke="${outline}" stroke-width="2.5" />
    </svg>`),
});

const ageMinutes = (user, nowMs) => (nowMs - new Date(user.timestamp).getTime()) / 60000;

const getMarkerAppearance = (user, nowMs) => {
    const color = user.is_guest ? GUEST_COLOR : GUIDE_COLOR;
    const age = ageMinutes(user, nowMs);
    if (age < FRESH_MINUTES) {
        // Fresh pins render as the top layer.
        return { icon: createSolidDotIcon(color), zIndex: 1000 };
    }
    if (age < RECENT_MINUTES) {
        return { icon: createOutlinedDotIcon(DARK_GRAY, color), zIndex: 500 };
    }
    return { icon: createOutlinedDotIcon(LIGHT_GRAY, color), zIndex: 100 };
};

const Legend = () => (
    <Box
        style={{
            position: 'absolute',
            bottom: '30px',
            left: '10px',
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
            padding: '8px 12px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
            zIndex: 10,
        }}
    >
        {[
            { color: GUEST_COLOR, label: 'Guest' },
            { color: GUIDE_COLOR, label: 'Guide' },
        ].map(({ color, label }) => (
            <Box key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="8" fill={color} />
                </svg>
                <Typography variant="caption">{label}</Typography>
            </Box>
        ))}
        <Typography variant="caption" color="textSecondary">
            Gray dots: last seen over 5 min ago
        </Typography>
    </Box>
);

const LiveLocationMap = () => {
    const { settings } = useContext(ContextStore);
    const dataAPI = useContext(DataAPIContext);
    const isSmallScreen = !useIsWidthUp('sm');

    const [users, setUsers] = useState([]);
    const [nowMs, setNowMs] = useState(Date.now());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [selectedTours, setSelectedTours] = useState([]);
    const [map, setMap] = useState(null);
    const [activeUserId, setActiveUserId] = useState(null);

    const shouldFitRef = useRef(true);
    const cancelledRef = useRef(false);

    const fetchLocations = useCallback(async () => {
        const params = new URLSearchParams();
        selectedGroups.forEach((g) => params.append('agroup', g.id));
        selectedTours.forEach((t) => params.append('tour', t.id));
        const qs = params.toString();
        try {
            const response = await dataAPI(GET_ONE, 'live_user_locations', { urlSuffix: qs ? `?${qs}` : '' });
            if (cancelledRef.current) return;
            setUsers(response.data?.users || []);
            setNowMs(Date.now());
            setError(null);
        } catch (e) {
            if (!cancelledRef.current) {
                setError('An error occurred while fetching live user locations.');
            }
        } finally {
            if (!cancelledRef.current) {
                setLoading(false);
            }
        }
    }, [dataAPI, selectedGroups, selectedTours]);

    // Initial fetch + 15s polling; restarts when the filters change.
    useEffect(() => {
        cancelledRef.current = false;
        setLoading(true);
        fetchLocations();
        const interval = setInterval(fetchLocations, POLL_INTERVAL * 1000);
        return () => {
            cancelledRef.current = true;
            clearInterval(interval);
        };
    }, [fetchLocations]);

    // Pins older than 3 hours are dropped server-side, but also age out
    // client-side between polls.
    const visibleUsers = users.filter((u) => ageMinutes(u, nowMs) < MAX_AGE_MINUTES);

    // Fit the map to the pins on first load and after a filter change.
    useEffect(() => {
        if (!map || !shouldFitRef.current || visibleUsers.length === 0) return;
        const bounds = new window.google.maps.LatLngBounds();
        visibleUsers.forEach((u) => bounds.extend({ lat: u.lat, lng: u.lng }));
        window.google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
            if (map.getZoom() > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM);
        });
        map.fitBounds(bounds, 60);
        shouldFitRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, users]);

    const handleGroupChange = (event, groups) => {
        shouldFitRef.current = true;
        setSelectedGroups(groups);
    };

    const handleTourChange = (event, tours) => {
        shouldFitRef.current = true;
        setSelectedTours(tours);
    };

    const activeUser = visibleUsers.find((u) => u.id === activeUserId);

    const containerStyles = {
        width: '100%',
        height: isSmallScreen ? 'calc(100vh - 18.5rem)' : 'calc(100vh - 15.5rem)',
        position: 'relative',
    };

    return (
        <>
            <PageMeta title="Live Map" />
            <FixedLinearProgress visibility={loading} />
            <Box p={isSmallScreen ? 1 : 2}>
                <PageHeading sx={{ mt: isSmallScreen ? 1 : 0, mb: 1 }}>Live Map</PageHeading>
                <Grid container spacing={isSmallScreen ? 1 : 2}>
                    <Grid item xs={12} lg={4}>
                        <RemoteAutocomplete
                            multiple
                            filterSelected
                            resource="access_group"
                            urlSuffix={null}
                            label="RBAC Groups"
                            variant="filled"
                            size="small"
                            fullWidth
                            resetQueryOnBlur
                            onChange={handleGroupChange}
                            value={selectedGroups}
                            getOptionLabel={(opt) => opt.name || `Access Group ID: ${opt.id}`}
                        />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <RemoteAutocomplete
                            multiple
                            filterSelected
                            resource="tour"
                            label="Tours"
                            variant="filled"
                            size="small"
                            fullWidth
                            resetQueryOnBlur
                            apiParams={{ fields: 'id,name,is_bespoke,start_date,end_date' }}
                            onChange={handleTourChange}
                            value={selectedTours}
                            getOptionLabel={(opt) => formatTourOptionLabel(opt)}
                        />
                    </Grid>
                    <Grid item xs={12} lg={4} style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" color="textSecondary">
                            {visibleUsers.length} live user{visibleUsers.length === 1 ? '' : 's'} · updates every {POLL_INTERVAL}s
                        </Typography>
                    </Grid>
                </Grid>
            </Box>
            {error && <Alert severity="error">{error}</Alert>}
            <div style={containerStyles}>
                <GoogleMapsReady apiKey={settings.google_maps_key}>
                    <GoogleMap
                        mapContainerStyle={MAP_CONTAINER_STYLE}
                        center={DEFAULT_CENTER}
                        zoom={2}
                        options={MAP_OPTIONS}
                        onLoad={(mapInstance) => setMap(mapInstance)}
                        onClick={() => setActiveUserId(null)}
                    >
                        {visibleUsers.map((user) => {
                            const { icon, zIndex } = getMarkerAppearance(user, nowMs);
                            return (
                                <Marker
                                    key={user.id}
                                    position={{ lat: user.lat, lng: user.lng }}
                                    icon={icon}
                                    onClick={() => setActiveUserId((prev) => (prev === user.id ? null : user.id))}
                                    options={{ optimized: false, zIndex: activeUserId === user.id ? 2000 : zIndex }}
                                />
                            );
                        })}
                        {activeUser && (
                            <OverlayView
                                position={{ lat: activeUser.lat, lng: activeUser.lng }}
                                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                getPixelPositionOffset={(width, height) => ({ x: 0, y: -(height + 30) })}
                            >
                                <div
                                    style={{
                                        background: 'white',
                                        padding: '10px',
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                                        pointerEvents: 'auto',
                                        width: '240px',
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Grid container alignItems="center" justifyContent="space-between" style={{ borderBottom: 'solid #D8D8D8 1px', marginBottom: '0.5rem', paddingBottom: '0.25rem' }}>
                                        <Grid item style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
                                                <circle cx="12" cy="12" r="8" fill={activeUser.is_guest ? GUEST_COLOR : GUIDE_COLOR} />
                                            </svg>
                                            <Typography variant="body2" fontWeight="bold">{activeUser.display_name}</Typography>
                                        </Grid>
                                        <Grid item>
                                            <IconButton onClick={() => setActiveUserId(null)} size="small">
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                    <Typography variant="body2">
                                        Last updated {moment(activeUser.timestamp).fromNow()}
                                    </Typography>
                                </div>
                            </OverlayView>
                        )}
                        <Legend />
                    </GoogleMap>
                </GoogleMapsReady>
            </div>
        </>
    );
};

export default LiveLocationMap;
